import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
    BooleanParam,
    DelimitedArrayParam,
    DelimitedNumericArrayParam,
    NumberParam,
    StringParam,
    useQueryParams,
} from "use-query-params";
import {
    createEnumDelimitedArrayParam,
    createEnumParam,
} from "serialize-query-params";
import { formatISO, parseISO } from "date-fns";
import { z } from "zod";
import { Entity } from "@common/types/entity";
import {
    AccountRolePresence,
    AccountRulePresence,
    AccountSource,
    AccountStatus,
} from "@common/types/domains/accounting/account";
import { AccountSubtype } from "@common/constants/domains/accounting/accounts";
import { ScheduleJeSubtype } from "@common/types/domains/accounting/schedule";
import { useScope } from "@state/scopeContext.lib";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { accountingLedgerBasisSchema } from "@common/schemas/domains/accounting/ledger.schema";
import { ClassFilterType } from "@common/types/class";
import {
    FORM_1099_BOX_FILTER_UNASSIGNED,
    Form1099Box,
    isForm1099BoxFilterValue,
} from "@common/constants/domains/accounting/form1099Box";
import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import {
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportCycle,
    AccountingReportRatioType,
    MoneyDirection,
} from "@common/constants";
import {
    isNativeInvoiceTermType,
    NativeInvoiceTermType,
} from "@common/helpers/invoice-terms.helper";
import { AmountRoundingLevel } from "@common/types/transactionRule";
import { InvoiceSource } from "@common/types/invoice";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { DateOnlyParam } from "@helpers/dateOnlyParam";
import { DATE_RANGE_PRESETS_BY_ID } from "@helpers/date";
import { useConstant } from "@hooks/useConstant";
import { useLedgers } from "@api/ledger.api";
import { useDefaultReportsFilters } from "../reports/useDefaultReportsFilters";
import {
    type AccountingDatePresetSource,
    resolveAccountingDatePreset,
    resolveKnownDatePreset,
} from "./accountingDatePreset";
import { AccountingReportFilters } from "./types";
import { useEntitiesWithAccountingAvailable } from "./useEntitiesWithAccountingAvailable";

function filterNullValues<T extends string | number>(
    values: Array<T | null> | null | undefined,
): T[] | undefined {
    const filtered = values?.filter((c): c is T => c !== null);
    return filtered?.length ? filtered : undefined;
}

function filterValuesByGuard<T extends string>(
    values: ReadonlyArray<string | null> | null | undefined,
    isValue: (value: string) => value is T,
): T[] | undefined {
    if (!values) {
        return undefined;
    }
    const filtered = values.filter(
        (value): value is T => value !== null && isValue(value),
    );
    return filtered.length > 0 ? filtered : undefined;
}

function filterNativeInvoiceTermTypes(
    values: ReadonlyArray<string | null> | null | undefined,
): NativeInvoiceTermType[] | undefined {
    return filterValuesByGuard(values, isNativeInvoiceTermType);
}

export type AccountingFiltersSetter = (
    setter: (current: AccountingReportFilters) => AccountingReportFilters,
) => void;

interface DatePeriod {
    startDate: Date | undefined;
    endDate: Date | undefined;
    datePreset: string | undefined;
}

interface SerializedDatePeriod {
    startDate: string;
    endDate: string;
    datePreset?: string;
}

const serializedDatePeriodSchema = z.object({
    startDate: z.string(),
    endDate: z.string(),
    datePreset: z.string().optional(),
});

export function useAccountingTab() {
    const [filtersQuery, setFiltersQuery] = useQueryParams({
        startDate: DateOnlyParam,
        endDate: DateOnlyParam,
        entityIds: DelimitedNumericArrayParam,
        entityId: NumberParam,
        accountCodes: DelimitedNumericArrayParam,
        accountStatus: StringParam,
        accountSource: StringParam,
        accountRolePresence: createEnumParam(
            Object.values(AccountRolePresence),
        ),
        accountRulePresence: createEnumParam(
            Object.values(AccountRulePresence),
        ),
        classes: DelimitedArrayParam,
        classFilterType: createEnumParam(Object.values(ClassFilterType)),
        isAllTime: BooleanParam,
        datePreset: StringParam,
        search: StringParam,
        ledgerId: StringParam,
        journalEntrySources: createEnumDelimitedArrayParam(
            Object.values(JournalEntrySourceFilter),
            ",",
        ),
        minAmount: NumberParam,
        maxAmount: NumberParam,
        direction: createEnumParam(Object.values(MoneyDirection)),
        amountRounded: BooleanParam,
        amountRoundingLevel: createEnumParam(
            Object.values(AmountRoundingLevel),
        ),
        counterpartiesIds: DelimitedArrayParam,
        withEmptyOrUnverifiedCounterparty: BooleanParam,
        hasCounterparty: BooleanParam,
        excludeFrom1099: BooleanParam,
        cycle: StringParam,
        comparisons: DelimitedArrayParam,
        comparisonDiffs: DelimitedArrayParam,
        ratioTypes: DelimitedArrayParam,
        accountSubtypes: DelimitedArrayParam,
        form1099Boxes: createEnumDelimitedArrayParam(
            [...Object.values(Form1099Box), FORM_1099_BOX_FILTER_UNASSIGNED],
            ",",
        ),
        consolidationGroupId: StringParam,
        transactionIds: DelimitedNumericArrayParam,
        scheduleApplicationIds: DelimitedArrayParam,
        scheduleJeSubtypes: createEnumDelimitedArrayParam(
            Object.values(ScheduleJeSubtype),
            ",",
        ),
        coaTemplateGroupKeys: DelimitedArrayParam,
        scheduleTemplateIds: DelimitedArrayParam,
        scheduleGroups: DelimitedArrayParam,
        holdingAccountIds: DelimitedArrayParam,
        termStartFrom: StringParam,
        termStartTo: StringParam,
        termEndFrom: StringParam,
        termEndTo: StringParam,
        termTypes: createEnumDelimitedArrayParam(
            Object.values(NativeInvoiceTermType),
            ",",
        ),
        accountIds: DelimitedNumericArrayParam,
        subaccountIds: DelimitedArrayParam,
        invoiceSources: createEnumDelimitedArrayParam(
            Object.values(InvoiceSource),
            ",",
        ),
        createdOnFrom: StringParam,
        createdOnTo: StringParam,
        dueDateFrom: StringParam,
        dueDateTo: StringParam,
        asOfDate: DateOnlyParam,
        customerId: StringParam,
    });

    const { workspaceId } = useScope();
    const { pathname } = useLocation();
    const [cachedEntityId, setCachedEntityId] = useWorkspaceUserCache<
        number | undefined
    >(WORKSPACE_USER_CACHE_KEYS.LAST_ENTITY_CACHE_KEY);

    const availableEntities = useEntitiesWithAccountingAvailable();
    const defaultReportsFilters = useDefaultReportsFilters();

    const validateEntityId = useCallback(
        (entityId?: number | null) => {
            if (!entityId) {
                return undefined;
            }

            return availableEntities.some(({ id }) => id === entityId)
                ? entityId
                : undefined;
        },
        [availableEntities],
    );

    if (
        filtersQuery.entityId &&
        !validateEntityId(filtersQuery.entityId) &&
        availableEntities.length > 0
    ) {
        setFiltersQuery({
            ...filtersQuery,
            entityId:
                validateEntityId(cachedEntityId) ?? availableEntities[0].id,
        });
    }

    const [cachedDatePeriod, setCachedDatePeriod] = useWorkspaceUserCache<
        SerializedDatePeriod | undefined
    >(
        WORKSPACE_USER_CACHE_KEYS.LAST_DATE_PERIOD_CACHE_KEY,
        serializedDatePeriodSchema,
    );

    const today = useConstant(() => new Date());
    const parsedCachedDatePeriod = useMemo((): DatePeriod | undefined => {
        if (!cachedDatePeriod) {
            return undefined;
        }
        const cachedPreset = cachedDatePeriod.datePreset
            ? DATE_RANGE_PRESETS_BY_ID[cachedDatePeriod.datePreset]
            : undefined;
        if (cachedPreset) {
            return {
                startDate: cachedPreset.getStart(today),
                endDate: cachedPreset.getEnd(today),
                datePreset: cachedDatePeriod.datePreset,
            };
        }
        // Older caches stored timestamps with offsets, which parse to another day in other timezones.
        return {
            startDate: parseISO(cachedDatePeriod.startDate.slice(0, 10)),
            endDate: parseISO(cachedDatePeriod.endDate.slice(0, 10)),
            datePreset: undefined,
        };
    }, [cachedDatePeriod, today]);

    // Note: separate date period from last entity as we believe
    // that in future date period will be different
    const [lastDatePeriod, setLastDatePeriod] = useState<
        DatePeriod | undefined
    >(parsedCachedDatePeriod);

    const [lastEntityId, setLastEntityId] = useState<number | undefined>(
        validateEntityId(cachedEntityId),
    );

    const [cachedLedgerBasis, setCachedLedgerBasis] = useWorkspaceUserCache<
        AccountingLedgerBasis | undefined
    >(
        WORKSPACE_USER_CACHE_KEYS.LAST_LEDGER_BASIS_CACHE_KEY,
        accountingLedgerBasisSchema,
    );

    // Mirror the cache in state: useWorkspaceUserCache reads only at mount and
    // this provider is never remounted, so the cached value alone goes stale.
    const [ledgerBasisState, setLedgerBasisState] = useState<
        AccountingLedgerBasis | undefined
    >(cachedLedgerBasis);

    const setLedgerBasis = useCallback(
        (basis: AccountingLedgerBasis) => {
            setLedgerBasisState(basis);
            setCachedLedgerBasis(basis);
        },
        [setCachedLedgerBasis],
    );

    // Re-seed from cache on workspace/user switch (provider isn't remounted).
    const [prevCachedLedgerBasis, setPrevCachedLedgerBasis] =
        useState(cachedLedgerBasis);
    if (cachedLedgerBasis !== prevCachedLedgerBasis) {
        setPrevCachedLedgerBasis(cachedLedgerBasis);
        setLedgerBasisState(cachedLedgerBasis);
    }

    const setFilters = useCallback(
        (
            setter: (
                current: AccountingReportFilters,
            ) => AccountingReportFilters,
        ) => {
            setFiltersQuery((current) => {
                const filteredComparisons =
                    filterNullValues<AccountingReportComparison>(
                        current.comparisons as
                            | Array<AccountingReportComparison | null>
                            | null
                            | undefined,
                    );

                const newFilters = setter({
                    search: current.search ?? undefined,
                    startDate: current.startDate ?? undefined,
                    endDate: current.endDate ?? undefined,
                    datePreset: current.datePreset ?? undefined,
                    entityId: current.entityId ?? undefined,
                    entityIds: filterNullValues(current.entityIds),
                    classes: filterNullValues(current.classes),
                    accountCodes: filterNullValues(current.accountCodes),
                    accountStatus:
                        (current.accountStatus as AccountStatus) ?? undefined,
                    accountSource:
                        (current.accountSource as AccountSource) ?? undefined,
                    accountRolePresence:
                        current.accountRolePresence ?? undefined,
                    accountRulePresence:
                        current.accountRulePresence ?? undefined,
                    ledgerId: current.ledgerId ?? undefined,
                    journalEntrySources:
                        filterNullValues<JournalEntrySourceFilter>(
                            current.journalEntrySources as
                                | Array<JournalEntrySourceFilter | null>
                                | null
                                | undefined,
                        ),
                    minAmount: current.minAmount ?? undefined,
                    maxAmount: current.maxAmount ?? undefined,
                    direction:
                        (current.direction as MoneyDirection) ?? undefined,
                    amountRounded: current.amountRounded ?? undefined,
                    amountRoundingLevel:
                        (current.amountRoundingLevel as AmountRoundingLevel) ??
                        undefined,
                    counterpartiesIds: filterNullValues(
                        current.counterpartiesIds,
                    ),
                    classFilterType: current.classFilterType ?? undefined,
                    isAllTime: current.isAllTime ?? undefined,
                    withEmptyOrUnverifiedCounterparty:
                        current.withEmptyOrUnverifiedCounterparty ?? undefined,
                    hasCounterparty: current.hasCounterparty ?? undefined,
                    excludeFrom1099: current.excludeFrom1099 ?? undefined,
                    cycle:
                        (current.cycle as AccountingReportCycle) ?? undefined,
                    comparisons: filteredComparisons,
                    comparisonDiffs:
                        (filteredComparisons?.length ?? 0) > 0
                            ? filterNullValues<AccountingReportComparisonDiff>(
                                  current.comparisonDiffs as
                                      | Array<AccountingReportComparisonDiff | null>
                                      | null
                                      | undefined,
                              )
                            : undefined,
                    ratioTypes: filterNullValues<AccountingReportRatioType>(
                        current.ratioTypes as
                            | Array<AccountingReportRatioType | null>
                            | null
                            | undefined,
                    ),
                    accountSubtypes: filterNullValues<AccountSubtype>(
                        current.accountSubtypes as
                            | Array<AccountSubtype | null>
                            | null
                            | undefined,
                    ),
                    form1099Boxes: filterValuesByGuard(
                        current.form1099Boxes,
                        isForm1099BoxFilterValue,
                    ),
                    consolidationGroupId:
                        current.consolidationGroupId ?? undefined,
                    coaTemplateGroupKeys: filterNullValues(
                        current.coaTemplateGroupKeys,
                    ),
                    scheduleTemplateIds: filterNullValues(
                        current.scheduleTemplateIds,
                    ),
                    scheduleGroups: filterNullValues(current.scheduleGroups),
                    holdingAccountIds: filterNullValues(
                        current.holdingAccountIds,
                    ),
                    termStartFrom: current.termStartFrom ?? undefined,
                    termStartTo: current.termStartTo ?? undefined,
                    termEndFrom: current.termEndFrom ?? undefined,
                    termEndTo: current.termEndTo ?? undefined,
                    termTypes: filterNativeInvoiceTermTypes(current.termTypes),
                    accountIds: filterNullValues(current.accountIds),
                    subaccountIds: filterNullValues(current.subaccountIds),
                    invoiceSources: filterNullValues<InvoiceSource>(
                        current.invoiceSources as
                            | Array<InvoiceSource | null>
                            | null
                            | undefined,
                    ),
                    createdOnFrom: current.createdOnFrom ?? undefined,
                    createdOnTo: current.createdOnTo ?? undefined,
                    dueDateFrom: current.dueDateFrom ?? undefined,
                    dueDateTo: current.dueDateTo ?? undefined,
                    asOfDate: current.asOfDate ?? undefined,
                    customerId: current.customerId ?? undefined,
                });

                if (newFilters.startDate && newFilters.endDate) {
                    // Report-local presets like "Through end of schedules" must not leak to other reports via this cache.
                    const knownDatePreset = resolveKnownDatePreset({
                        presetId: newFilters.datePreset,
                        startDate: newFilters.startDate,
                        endDate: newFilters.endDate,
                    });
                    // Persist calendar dates, not instants: a full timestamp
                    // with offset re-parsed in another timezone lands on the
                    // previous/next day, shifting the cached period.
                    setCachedDatePeriod({
                        startDate: formatISO(newFilters.startDate, {
                            representation: "date",
                        }),
                        endDate: formatISO(newFilters.endDate, {
                            representation: "date",
                        }),
                        datePreset: knownDatePreset,
                    });
                    setLastDatePeriod({
                        startDate: newFilters.startDate,
                        endDate: newFilters.endDate,
                        datePreset: knownDatePreset,
                    });
                }
                if (newFilters.entityId) {
                    setCachedEntityId(newFilters.entityId);
                    setLastEntityId(newFilters.entityId);
                }
                return newFilters;
            });
        },
        [
            setFiltersQuery,
            setCachedEntityId,
            setLastEntityId,
            setCachedDatePeriod,
            setLastDatePeriod,
        ],
    );

    const cleanFilters = useMemo(() => {
        const filteredComparisons =
            filterNullValues<AccountingReportComparison>(
                filtersQuery.comparisons as
                    | Array<AccountingReportComparison | null>
                    | null
                    | undefined,
            );

        const hasUrlDates = Boolean(
            filtersQuery.startDate && filtersQuery.endDate,
        );
        const startDate = !filtersQuery.isAllTime
            ? (filtersQuery.startDate ??
              lastDatePeriod?.startDate ??
              defaultReportsFilters.start ??
              undefined)
            : undefined;
        const endDate = !filtersQuery.isAllTime
            ? (filtersQuery.endDate ??
              lastDatePeriod?.endDate ??
              defaultReportsFilters.end ??
              undefined)
            : undefined;
        let datePresetSource: AccountingDatePresetSource;
        if (filtersQuery.isAllTime) {
            datePresetSource = { type: "allTime" };
        } else if (hasUrlDates) {
            datePresetSource = {
                type: "urlDates",
                presetId: filtersQuery.datePreset ?? undefined,
            };
        } else {
            datePresetSource = {
                type: "implicitDates",
                queryPreset: filtersQuery.datePreset ?? undefined,
                rememberedDatePeriod: lastDatePeriod
                    ? { presetId: lastDatePeriod.datePreset }
                    : undefined,
            };
        }

        return {
            search: filtersQuery.search ?? undefined,
            startDate,
            endDate,
            datePreset: resolveAccountingDatePreset({
                source: datePresetSource,
                startDate,
                endDate,
            }),
            entityId: filtersQuery.entityId ?? lastEntityId ?? undefined,
            entityIds: filterNullValues(filtersQuery.entityIds),
            classes: filterNullValues(filtersQuery.classes),
            classFilterType: filtersQuery.classFilterType ?? undefined,
            accountCodes: filterNullValues(filtersQuery.accountCodes),
            accountStatus:
                (filtersQuery.accountStatus as AccountStatus) ?? undefined,
            accountSource:
                (filtersQuery.accountSource as AccountSource) ?? undefined,
            accountRolePresence: filtersQuery.accountRolePresence ?? undefined,
            accountRulePresence: filtersQuery.accountRulePresence ?? undefined,
            ledgerId: filtersQuery.ledgerId ?? undefined,
            journalEntrySources: filterNullValues<JournalEntrySourceFilter>(
                filtersQuery.journalEntrySources as
                    | Array<JournalEntrySourceFilter | null>
                    | null
                    | undefined,
            ),
            minAmount: filtersQuery.minAmount ?? undefined,
            maxAmount: filtersQuery.maxAmount ?? undefined,
            direction: (filtersQuery.direction as MoneyDirection) ?? undefined,
            amountRounded: filtersQuery.amountRounded ?? undefined,
            amountRoundingLevel:
                (filtersQuery.amountRoundingLevel as AmountRoundingLevel) ??
                undefined,
            counterpartiesIds: filterNullValues(filtersQuery.counterpartiesIds),
            withEmptyOrUnverifiedCounterparty:
                filtersQuery.withEmptyOrUnverifiedCounterparty ?? undefined,
            hasCounterparty: filtersQuery.hasCounterparty ?? undefined,
            excludeFrom1099: filtersQuery.excludeFrom1099 ?? undefined,
            cycle: (filtersQuery.cycle as AccountingReportCycle) ?? undefined,
            comparisons: filteredComparisons,
            comparisonDiffs:
                (filteredComparisons?.length ?? 0) > 0
                    ? filterNullValues<AccountingReportComparisonDiff>(
                          filtersQuery.comparisonDiffs as
                              | Array<AccountingReportComparisonDiff | null>
                              | null
                              | undefined,
                      )
                    : undefined,
            ratioTypes: filterNullValues<AccountingReportRatioType>(
                filtersQuery.ratioTypes as
                    | Array<AccountingReportRatioType | null>
                    | null
                    | undefined,
            ),
            accountSubtypes: filterNullValues<AccountSubtype>(
                filtersQuery.accountSubtypes as
                    | Array<AccountSubtype | null>
                    | null
                    | undefined,
            ),
            form1099Boxes: filterValuesByGuard(
                filtersQuery.form1099Boxes,
                isForm1099BoxFilterValue,
            ),
            isAllTime: filtersQuery.isAllTime ?? undefined,
            consolidationGroupId:
                filtersQuery.consolidationGroupId ?? undefined,
            // workspaceId only travels with the request when consolidating; the
            // entity-scoped routes otherwise resolve the workspace from entityId.
            workspaceId: filtersQuery.consolidationGroupId
                ? (workspaceId ?? undefined)
                : undefined,
            transactionIds:
                filterNullValues(filtersQuery.transactionIds) ?? undefined,
            scheduleApplicationIds:
                filterNullValues(filtersQuery.scheduleApplicationIds) ??
                undefined,
            scheduleJeSubtypes:
                filterNullValues(filtersQuery.scheduleJeSubtypes) ?? undefined,
            coaTemplateGroupKeys:
                filterNullValues(filtersQuery.coaTemplateGroupKeys) ??
                undefined,
            scheduleTemplateIds: filterNullValues(
                filtersQuery.scheduleTemplateIds,
            ),
            scheduleGroups: filterNullValues(filtersQuery.scheduleGroups),
            holdingAccountIds: filterNullValues(filtersQuery.holdingAccountIds),
            termStartFrom: filtersQuery.termStartFrom ?? undefined,
            termStartTo: filtersQuery.termStartTo ?? undefined,
            termEndFrom: filtersQuery.termEndFrom ?? undefined,
            termEndTo: filtersQuery.termEndTo ?? undefined,
            termTypes: filterNativeInvoiceTermTypes(filtersQuery.termTypes),
            accountIds: filterNullValues(filtersQuery.accountIds),
            subaccountIds: filterNullValues(filtersQuery.subaccountIds),
            invoiceSources: filterNullValues<InvoiceSource>(
                filtersQuery.invoiceSources as
                    | Array<InvoiceSource | null>
                    | null
                    | undefined,
            ),
            createdOnFrom: filtersQuery.createdOnFrom ?? undefined,
            createdOnTo: filtersQuery.createdOnTo ?? undefined,
            dueDateFrom: filtersQuery.dueDateFrom ?? undefined,
            dueDateTo: filtersQuery.dueDateTo ?? undefined,
            asOfDate: filtersQuery.asOfDate ?? undefined,
            customerId: filtersQuery.customerId ?? undefined,
        };
    }, [
        filtersQuery,
        lastDatePeriod,
        lastEntityId,
        defaultReportsFilters,
        workspaceId,
    ]);

    const selectedEntity = useMemo(
        () => availableEntities.find((e) => e.id === cleanFilters.entityId),
        [availableEntities, cleanFilters.entityId],
    );
    const { data: selectedEntityLedgers } = useLedgers(selectedEntity?.id);

    // The General Ledger page owns ledger resolution across its selected entity
    // set (single or multi) via useGeneralLedgerLedger. Multi-select pages (e.g.
    // Chart of Accounts) don't render a ledger-scoped report. The consolidated
    // elimination reports are group-scoped and never use a single-entity ledger.
    // In all cases the raw ledgerId is passed through untouched here. Keying off
    // the route rather than the presence of `entityIds` keeps single-entity
    // reports (P&L, Balance Sheet, …) resolving even if a stale `entityIds`
    // reaches their URL — and stops a drill-down away from a consolidated
    // elimination report (which drops consolidationGroupId but carries entityId)
    // from resolving a ledger and writing it back onto the current URL, which
    // would clobber the in-flight navigation.
    const ownsLedgerSelectionExternally =
        pathname.includes("/general-ledger") ||
        pathname.includes("/chart-of-accounts") ||
        pathname.includes("/elimination-") ||
        pathname.includes("/accounting/schedules");

    // Single-entity reports (P&L, Balance Sheet, Trial Balance, …) read this
    // resolved ledgerId so the report never fires with a stale ledgerId from a
    // previously selected entity, which the backend rejects with "Ledger not
    // accessible". Resolution is synchronous (unlike the previous post-render
    // reset effect, which let the report fire once with the stale value first).
    const resolvedLedgerId = useMemo(() => {
        // Consolidated reports (and any state with no entity scope) manage the
        // ledger differently; pass the raw value through.
        if (
            ownsLedgerSelectionExternally ||
            cleanFilters.consolidationGroupId ||
            !cleanFilters.entityId
        ) {
            return cleanFilters.ledgerId;
        }
        // Withhold the report until the selected entity's ledgers are loaded
        // (which also covers the window where the entity itself is still
        // loading), so it never fires with a ledgerId left over from a
        // previously selected entity (rejected as "Ledger not accessible").
        if (!selectedEntityLedgers || selectedEntityLedgers.length === 0) {
            return undefined;
        }
        const isCurrentLedgerValid = selectedEntityLedgers.some(
            (ledger) => ledger.id === cleanFilters.ledgerId,
        );
        if (isCurrentLedgerValid) {
            return cleanFilters.ledgerId;
        }
        const cachedBasisLedger = ledgerBasisState
            ? selectedEntityLedgers.find(
                  (ledger) => ledger.basis === ledgerBasisState,
              )
            : undefined;
        const defaultLedger =
            cachedBasisLedger ??
            selectedEntityLedgers.find(
                (ledger) => ledger.basis === AccountingLedgerBasis.cash,
            ) ??
            selectedEntityLedgers[0];
        return defaultLedger?.id;
    }, [
        ownsLedgerSelectionExternally,
        cleanFilters.consolidationGroupId,
        cleanFilters.entityId,
        cleanFilters.ledgerId,
        selectedEntityLedgers,
        ledgerBasisState,
    ]);

    // Keep the URL ledgerId aligned with the resolved value (for refresh,
    // sharing and drill-down links). The override above already makes the
    // in-memory ledgerId valid, so this never gates the report query.
    useEffect(() => {
        if (ownsLedgerSelectionExternally || !resolvedLedgerId) {
            return;
        }
        // Persist the displayed basis so it carries across reports; a Cash fallback becomes the new preference.
        const resolvedLedger = selectedEntityLedgers?.find(
            (ledger) => ledger.id === resolvedLedgerId,
        );
        if (resolvedLedger && resolvedLedger.basis !== ledgerBasisState) {
            setLedgerBasis(resolvedLedger.basis);
        }
        if (resolvedLedgerId !== cleanFilters.ledgerId) {
            setFiltersQuery({ ledgerId: resolvedLedgerId });
        }
    }, [
        ownsLedgerSelectionExternally,
        resolvedLedgerId,
        cleanFilters.ledgerId,
        setFiltersQuery,
        selectedEntityLedgers,
        setLedgerBasis,
        ledgerBasisState,
    ]);

    const filters = useMemo(
        () => ({ ...cleanFilters, ledgerId: resolvedLedgerId }),
        [cleanFilters, resolvedLedgerId],
    );

    useEffect(() => {
        if (
            !selectedEntity &&
            availableEntities.length > 0 &&
            workspaceId &&
            availableEntities[0].workspaceId === workspaceId
        ) {
            const cachedEntity = availableEntities.find(
                (e) => e.id === cachedEntityId,
            );
            setFiltersQuery({
                entityId: cachedEntity
                    ? cachedEntity.id
                    : availableEntities[0].id,
                startDate:
                    parsedCachedDatePeriod?.startDate ?? filtersQuery.startDate,
                endDate:
                    parsedCachedDatePeriod?.endDate ?? filtersQuery.endDate,
                datePreset: parsedCachedDatePeriod
                    ? parsedCachedDatePeriod.datePreset
                    : filtersQuery.datePreset,
            });
        }
    }, [
        setCachedEntityId,
        filtersQuery.entityId,
        selectedEntity,
        availableEntities,
        setFiltersQuery,
        cachedEntityId,
        workspaceId,
        parsedCachedDatePeriod,
        filtersQuery.startDate,
        filtersQuery.endDate,
        filtersQuery.datePreset,
    ]);

    return {
        filters,
        setFilters,
        selectedEntity,
        ledgerBasis: ledgerBasisState,
        setLedgerBasis,
    };
}

interface AccountingTabContextType {
    filters: AccountingReportFilters;
    setFilters: AccountingFiltersSetter;
    selectedEntity: Entity | undefined;
    ledgerBasis: AccountingLedgerBasis | undefined;
    setLedgerBasis: (basis: AccountingLedgerBasis) => void;
}

export const AccountingTabContext = createContext<AccountingTabContextType>({
    filters: {},
    setFilters: () => {},
    selectedEntity: undefined,
    ledgerBasis: undefined,
    setLedgerBasis: () => {},
});

export const useAccountingTabContext = () => useContext(AccountingTabContext);
